import type { Announcement, Breadcrumb, EnvSnapshot, Identity, InitOptions, RecentRequest, RemoteConfig, Snapshot } from '../types';
import { RingBuffer } from './buffer';
import { isHiddenUrl } from './visibility';
import { Redactor } from './redactor';
import { randomId, readJson, storage, writeJson } from './util';

declare const __SDK_VERSION__: string;
export const SDK_VERSION: string = typeof __SDK_VERSION__ === 'string' ? __SDK_VERSION__ : '0.0.0';

export const DEFAULTS = {
  apiUrl: 'https://api.fetech.llc/v1',
  position: 'bottom-left' as const,
  locale: 'tr' as const,
  captureNetwork: true,
  captureBodies: true,
  captureConsole: true,
  captureConsoleLog: false,
  maxBodySize: 16 * 1024,
  launcher: true,
  hideOnAuthPages: true,
  hideOnPaths: [] as string[],
  debug: false,
};

export type Resolved = Required<Omit<InitOptions, 'env' | 'redact' | 'allowUrls' | 'denyUrls' | 'theme'>> &
  Pick<InitOptions, 'env' | 'redact' | 'allowUrls' | 'denyUrls' | 'theme'>;

const TOKENS_KEY = 'fetrace:tickets';
const SESSION_KEY = 'fetrace:session';
const DISMISSED_KEY = 'fetrace:dismissed';
const SEEN_KEY = 'fetrace:seen';
const CONFIG_KEY = 'fetrace:config';

/** Everything the SDK knows at runtime. One instance per page. */
export class State {
  options!: Resolved;
  identity: Identity | null = null;
  context: Record<string, unknown> = {};
  remote: RemoteConfig = {};
  redactor = new Redactor();
  breadcrumbs = new RingBuffer<Breadcrumb>(50);
  requests = new RingBuffer<RecentRequest>(20);
  jsErrors = new RingBuffer<{ message: string; stack?: string; at: string }>(20);
  sessionKey: string;
  readonly local = storage('local');
  readonly session = storage('session');
  initialized = false;

  constructor() {
    this.sessionKey = this.session?.getItem(SESSION_KEY) ?? '';
    if (!this.sessionKey) {
      this.sessionKey = randomId(32);
      this.session?.setItem(SESSION_KEY, this.sessionKey);
    }
  }

  configure(options: InitOptions): void {
    this.options = { ...DEFAULTS, ...stripUndefined(options) } as Resolved;
    this.options.apiUrl = this.options.apiUrl.replace(/\/+$/, '');
    this.redactor = new Redactor({ patterns: options.redact, maxBodySize: this.options.maxBodySize });
    this.initialized = true;
  }

  applyRemote(remote: RemoteConfig): void {
    this.remote = remote;
    if (remote.redact) this.redactor.extend({ fields: remote.redact.fields, headers: remote.redact.headers });
    if (remote.capture_bodies === false) this.options.captureBodies = false;
    if (remote.limits?.max_body_bytes) this.redactor.extend({ maxBodySize: Math.min(this.options.maxBodySize, remote.limits.max_body_bytes) });
  }

  /** Last good /config answer, per key (announcements left out: they go stale and /status refreshes them). */
  rememberRemote(remote: RemoteConfig): void {
    writeJson(this.local, CONFIG_KEY, { key: this.options.key, remote: { ...remote, announcements: undefined } });
  }

  cachedRemote(): RemoteConfig | null {
    const c = readJson<{ key?: string; remote?: RemoteConfig } | null>(this.local, CONFIG_KEY, null);
    return c?.key === this.options.key && c.remote ? c.remote : null;
  }

  /**
   * Pages the UI stays off — login / register / password by default (DEFAULTS.hideOnAuthPages).
   * The dashboard setting wins over the script tag, the same way placement does.
   */
  uiHidden(url: string = typeof location !== 'undefined' ? location.href : ''): boolean {
    if (!url) return false;
    const v = this.remote.visibility ?? {};

    return isHiddenUrl(url, {
      authPages: v.hide_on_auth_pages ?? this.options.hideOnAuthPages,
      paths: [...this.options.hideOnPaths, ...(v.hide_on_paths ?? [])],
    });
  }

  get apiOrigin(): string {
    try {
      return new URL(this.options.apiUrl).origin;
    } catch {
      return '';
    }
  }

  isOwnApi(url: string): boolean {
    return url.startsWith(this.options.apiUrl) || (this.apiOrigin !== '' && url.startsWith(this.apiOrigin));
  }

  /** Should X-FE-Trace-Id be attached and the exchange captured? Default: same-origin only. */
  shouldTrace(url: string): boolean {
    const { allowUrls, denyUrls } = this.options;
    if (denyUrls?.some((r) => r.test(url))) return false;
    if (allowUrls && allowUrls.length) return allowUrls.some((r) => r.test(url));
    try {
      return new URL(url, location.href).origin === location.origin;
    } catch {
      return false;
    }
  }

  env(): EnvSnapshot {
    return {
      url: location.href,
      referrer: document.referrer,
      user_agent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language,
      timezone: (() => {
        try {
          return Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch {
          return '';
        }
      })(),
      online: navigator.onLine,
      sdk_version: SDK_VERSION,
      env: this.options.env,
    };
  }

  snapshot(): Snapshot {
    return {
      env: this.env(),
      breadcrumbs: this.breadcrumbs.toArray(),
      recent_requests: this.requests.toArray(),
      js_errors: this.jsErrors.toArray(),
      context: { ...this.context },
    };
  }

  sessionPayload() {
    const e = this.env();
    return { key: this.sessionKey, user_agent: e.user_agent, viewport: e.viewport, locale: e.language, timezone: e.timezone };
  }

  userPayload(): Identity | null {
    return this.identity ? { ...this.identity, id: String(this.identity.id) } : null;
  }

  announcements(): Announcement[] {
    return this.remote.announcements ?? [];
  }

  setAnnouncements(list: Announcement[]): void {
    this.remote = { ...this.remote, announcements: list };
  }

  dismissedAnnouncements(): number[] {
    return readJson<number[]>(this.local, DISMISSED_KEY, []);
  }

  dismissAnnouncement(id: number): void {
    writeJson(this.local, DISMISSED_KEY, Array.from(new Set([...this.dismissedAnnouncements(), id])).slice(-50));
  }

  /** Announcements the user has looked at (status tab opened) — drives the "Durum" tab badge and the launcher glow. */
  seenAnnouncements(): number[] {
    return readJson<number[]>(this.local, SEEN_KEY, []);
  }

  markAnnouncementsSeen(ids: number[]): void {
    if (!ids.length) return;
    writeJson(this.local, SEEN_KEY, Array.from(new Set([...this.seenAnnouncements(), ...ids])).slice(-50));
  }

  /** Active announcements that were neither dismissed from the band nor viewed on the status tab. */
  unseenAnnouncements(): Announcement[] {
    const dismissed = this.dismissedAnnouncements();
    const seen = this.seenAnnouncements();
    return this.announcements().filter((a) => !dismissed.includes(a.id) && !seen.includes(a.id));
  }

  ticketTokens(): Record<string, string> {
    return readJson<Record<string, string>>(this.local, TOKENS_KEY, {});
  }

  rememberTicket(id: number, token: string): void {
    const all = this.ticketTokens();
    all[String(id)] = token;
    writeJson(this.local, TOKENS_KEY, all);
  }

  forgetTickets(): void {
    this.local?.removeItem(TOKENS_KEY);
  }

  reset(): void {
    this.identity = null;
    this.context = {};
    this.forgetTickets();
  }

  log(...args: unknown[]): void {
    if (this.options?.debug) console.debug('[FETrace]', ...args);
  }
}

function stripUndefined<T extends object>(obj: T): T {
  const out = {} as T;
  for (const k of Object.keys(obj) as (keyof T)[]) if (obj[k] !== undefined) out[k] = obj[k];
  return out;
}
